import { useCallback, useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { useVideoStatusSocket } from "../../hooks/useVideoStatusSocket"
import { VIDEO_STATUS_LABELS, type VideoStatus } from "../../types/video"
import { EyeIcon, PencilIcon, RefreshIcon, TrashBinIcon } from "../../icons"

type Video = {
  id: string
  title: string
  description?: string
  status: VideoStatus
  duration?: number
  file_size?: number
  processing_progress?: number
  stream_url?: string
  error_message?: string | null
  lesson?: {
    id: string
    title: string
    course_title: string
  }
}

type VideoOptions = {
  courses: { id: string; title: string; lessons: { id: string; title: string }[] }[]
}

type StatusFilter = "all" | VideoStatus

const STATUS_COLORS: Record<VideoStatus, string> = {
  ready: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  queued: "bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300",
  processing: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  transcribing: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300",
  uploading: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  error: "bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300",
}

function formatBytes(bytes?: number): string {
  if (!bytes) return "—"
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDuration(seconds?: number): string {
  if (!seconds) return "—"
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

export default function VideosManager() {
  const navigate = useNavigate()
  const [options, setOptions] = useState<VideoOptions>({ courses: [] })
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [search, setSearch] = useState("")  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null)
  const qc = useQueryClient()

  const {
    rows: videos,
    total: videosTotal,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error: loadError,
    reload,
    queryKey,
  } = useServerTable<Video>({
    endpoint: "/academy/admin/videos",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    params: { status: statusFilter === "all" ? undefined : statusFilter, search },
    extractRows: (data) => data.videos ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  useEffect(() => {
    api
      .get("/academy/admin/videos/options")
      .then((res) => setOptions(res.data.data ?? { courses: [] }))
      .catch(() => setOptions({ courses: [] }))
  }, [])

  // Patchea únicamente la fila afectada en la cache de react-query — nunca
  // un refetch de tabla completa, para no perder scroll/selección/foco
  // mientras se valida o asigna un video. Reemplaza el polling de 5s previo.
  const patchVideoRow = useCallback(
    (videoId: string, patch: Partial<Pick<Video, "status" | "error_message" | "duration">>) => {
      qc.setQueryData<{ videos: Video[]; pagination?: { total: number } }>(queryKey, (prev) => {
        if (!prev) return prev
        return { ...prev, videos: prev.videos.map((v) => (v.id === videoId ? { ...v, ...patch } : v)) }
      })
    },
    [qc, queryKey]
  )

  useVideoStatusSocket({
    onEvent: (event) => {
      patchVideoRow(event.videoId, { status: event.status, error_message: event.errorMessage, duration: event.duration })
      if (event.status === "ready") {
        toastSuccess("Un video terminó de procesarse y ya está listo.")
      } else if (event.status === "error") {
        toastError(event.errorMessage ?? "Un video falló al procesarse.")
      }
    },
    onFallbackPoll: () => {
      // Red de seguridad si el WebSocket nunca logra conectar (ej. proxy
      // corporativo que lo bloquea): refresca fila por fila los videos aún
      // en proceso, nunca la tabla completa.
      const busy = videos.filter((v) => v.status === "processing" || v.status === "uploading" || v.status === "transcribing" || v.status === "queued")
      for (const v of busy) {
        api
          .get(`/academy/admin/videos/${v.id}`)
          .then((res) => {
            const updated = res.data?.data
            if (updated) patchVideoRow(v.id, { status: updated.status, error_message: updated.error_message, duration: updated.duration })
          })
          .catch(() => {})
      }
    },
  })

  async function onRetry(video: Video) {
    setRetryingId(video.id)
    try {
      await api.post(`/academy/admin/videos/${video.id}/process`)
      toastSuccess("Reprocesamiento encolado.")
      await reload()
    } catch (err) {
      toastError(err)
    } finally {
      setRetryingId(null)
    }
  }

  async function onDelete(video: Video) {
    setDeletingId(video.id)
    try {
      await api.delete(`/academy/admin/videos/${video.id}`)
      toastSuccess("Video eliminado.")
      await reload()
    } catch (err) {
      toastError(err)
    } finally {
      setDeletingId(null)
      setVideoToDelete(null)
    }
  }

  const columns: DataTableColumn<Video>[] = [
    {
      key: "title",
      label: "Video",
      sortable: true,
      render: (video) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{video.title}</p>
          {video.lesson ? (
            <p className="text-xs text-gray-500">
              {video.lesson.course_title} › {video.lesson.title}
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">Sin asociar</p>
          )}
        </>
      ),
    },
    {
      key: "status",
      label: "Estado",
      sortable: true,
      render: (video) => (
        <span
          className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[video.status]}`}
          title={video.status === "error" ? (video.error_message ?? undefined) : undefined}
        >
          {VIDEO_STATUS_LABELS[video.status]}
        </span>
      ),
    },
    {
      key: "duration",
      label: "Duración",
      sortable: true,
      render: (video) => <span className="text-xs text-gray-500">{formatDuration(video.duration)}</span>,
    },
    {
      key: "file_size",
      label: "Tamaño",
      render: (video) => <span className="text-xs text-gray-500">{formatBytes(video.file_size)}</span>,
    },
    {
      key: "actions",
      label: "Acción",
      render: (video) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              { label: "Editar", icon: <PencilIcon />, onClick: () => navigate(`/instructor/videos/${video.id}/edit`), requires: "academy:videos:edit" },
              ...(video.status === "ready" && video.stream_url
                ? [
                    {
                      label: "Ver video",
                      icon: <EyeIcon />,
                      onClick: () => window.open(video.stream_url, "_blank", "noopener,noreferrer"),
                    },
                  ]
                : []),
              ...(video.status === "error"
                ? [
                    {
                      label: retryingId === video.id ? "Reintentando..." : "Reintentar",
                      icon: <RefreshIcon />,
                      onClick: () => void onRetry(video),
                      disabled: retryingId === video.id,
                      requires: "academy:videos:edit" as const,
                    },
                  ]
                : []),
              ...(video.status === "uploading" || video.status === "error"
                ? [
                    {
                      label: deletingId === video.id ? "Eliminando..." : "Eliminar",
                      icon: <TrashBinIcon />,
                      onClick: () => setVideoToDelete(video),
                      variant: "danger" as const,
                      disabled: deletingId === video.id,
                      requires: "academy:videos:edit" as const,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", "Videos"]}
      title="Videos"
      subtitle="Gestiona y sube videos del catálogo"
      tabs={[{ label: "Videos", to: "/instructor/videos", active: true }]}
      metrics={[
        { label: "Total videos (filtro)", value: String(videosTotal) },
        { label: "En esta página", value: String(videos.length) },
        {
          label: "Procesando/subiendo",
          value: String(videos.filter((v) => v.status === "processing" || v.status === "uploading" || v.status === "transcribing" || v.status === "queued").length),
          tone:
            videos.filter((v) => v.status === "processing" || v.status === "uploading" || v.status === "transcribing" || v.status === "queued").length > 0
              ? "warning"
              : "neutral",
          hint: "Aún no disponibles para los estudiantes",
        },
        { label: "Cursos disponibles", value: String(options.courses.length) },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar video por título...",
        },
        filters: [
          {
            type: "select",
            key: "status",
            label: "Estado",
            value: statusFilter,
            onChange: (value) => setStatusFilter(value as StatusFilter),
            options: [
              { label: "Todos", value: "all" },
              { label: "Listos", value: "ready" },
              { label: "Procesando", value: "processing" },
              { label: "Transcribiendo", value: "transcribing" },
              { label: "Subiendo", value: "uploading" },
              { label: "Error", value: "error" },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,        onCreate: () => navigate("/instructor/videos/upload"),
        createLabel: "+ Subir video",
        requires: "academy:videos:edit",
      }}
    >
      {loadError && (
        <p role="alert" aria-live="assertive" className="text-error-600 dark:text-error-400 text-sm mb-4">
          {loadError}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={videos}
        rowKey={(video) => video.id}
        loading={loading}
        emptyMessage="No hay videos en esta categoría."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={videosTotal}
        onPageChange={setPage}
      />      <ConfirmDialog
        isOpen={videoToDelete !== null}
        title={`¿Eliminar el video "${videoToDelete?.title ?? ""}"?`}
        message="Esta subida no llegó a completarse."
        confirmLabel="Eliminar"
        confirming={deletingId === videoToDelete?.id}
        onConfirm={() => videoToDelete && void onDelete(videoToDelete)}
        onCancel={() => setVideoToDelete(null)}
      />
    </ModuleLayout>
  )
}
